import React, { useState } from 'react';
import { useT } from '../../../i18n/LanguageContext';
import { errorText } from '../responseSafety';

/** The Customer API omits QuickBooks' saved Cc field, so statement copies are explicit admin data. */
export default function StatementCcEditor({ statement, onSave }) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const emails = Array.isArray(statement.statementCcEmails) ? statement.statementCcEmails : [];

  const start = () => {
    setDraft(emails.join('\n'));
    setError('');
    setEditing(true);
  };
  const save = async (event) => {
    event.preventDefault();
    const next = draft
      .split(/[,;\n]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (next.length > 5) {
      setError(t('Use at most five CC email addresses.'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onSave(statement, next);
      setEditing(false);
    } catch (e) {
      setError(t(errorText(e, 'The CC recipients were not saved. Please try again.')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-3 rounded-lg border border-[#dbe7f3] bg-white px-4 py-3"
      aria-label={t('Statement CC recipients')}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-[13px] font-semibold text-[#0f172a]">
            {t('Statement CC recipients')}
          </h3>
          <p className="mt-0.5 text-[12px] text-[#64748b]">
            {t(
              'QuickBooks sync brings in the primary email only. Add its Cc addresses here for MedEffects statements.'
            )}
          </p>
        </div>
        {!editing && statement.clinicId && (
          <button
            type="button"
            onClick={start}
            className="text-[13px] font-medium text-[#0089ff] hover:underline"
          >
            {t(emails.length ? 'Edit CC' : 'Add CC')}
          </button>
        )}
      </div>
      {!editing ? (
        <p className="mt-2 text-[13px] text-[#334155] break-all">
          {emails.length ? emails.join(', ') : t('No additional recipients')}
        </p>
      ) : (
        <form onSubmit={save} className="mt-3">
          <label
            htmlFor={`statement-cc-${statement.qbCustomerId}`}
            className="block text-[12px] font-medium text-[#334155]"
          >
            {t('CC email addresses')}
          </label>
          <textarea
            id={`statement-cc-${statement.qbCustomerId}`}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={2}
            disabled={busy}
            placeholder={t('One address per line, or separated by commas')}
            className="mt-1 w-full rounded-lg border border-[#cbd5e1] px-3 py-2 text-[13px] text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 disabled:opacity-60"
          />
          {error && (
            <p role="alert" className="mt-1 text-[12px] text-[#b42318]">
              {error}
            </p>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing(false)}
              className="rounded-lg border border-[#dbe7f3] px-3 py-1.5 text-[13px] text-[#475569] hover:bg-[#f8fafc] disabled:opacity-60"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded-lg bg-[#2563eb] px-3 py-1.5 text-[13px] font-medium text-white hover:bg-[#1d4ed8] disabled:opacity-60"
            >
              {busy ? t('Saving...') : t('Save recipients')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
